import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminService } from '@/services/adminApi';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, RefreshCw, CheckCircle, ChevronLeft, ChevronRight,
  Clock, Menu, Eye, XCircle, Info
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminErrors() {
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const { colors, isDark } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unresolved, resolved
  const limit = 20;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadErrors();
    }
  }, [isAuthenticated, page, filter]);

  const loadErrors = async () => {
    try {
      setLoading(true);
      const res = await adminService.getErrors({ 
        limit, 
        offset: page * limit,
        resolved: filter === 'all' ? undefined : filter === 'resolved'
      });
      setErrors(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (errorId, resolved) => {
    try {
      await adminService.updateError(errorId, { resolved });
      toast.success(resolved ? 'Marqué comme résolu' : 'Marqué comme non résolu');
      loadErrors();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-500',
      high: 'bg-orange-500/20 text-orange-500',
      medium: 'bg-yellow-500/20 text-yellow-500',
      low: 'bg-blue-500/20 text-blue-500'
    };
    return (
      <Badge className={styles[severity] || styles.medium}>
        {severity?.toUpperCase() || 'MEDIUM'}
      </Badge>
    );
  };

  const getErrorTypeIcon = (type) => {
    switch (type) {
      case 'api_error': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'validation_error': return <Info className="w-4 h-4 text-yellow-500" />;
      case 'database_error': return <XCircle className="w-4 h-4 text-orange-500" />;
      default: return <AlertTriangle className="w-4 h-4 text-slate-500" />;
    }
  };

  if (authLoading) {
    return (
      <div className={`min-h-screen ${colors.bg} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-[#5BA946] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bg} flex`}>
      <AdminSidebar 
        currentPath={location.pathname}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className={`${colors.bgHeader} border-b ${colors.border} p-4 sticky top-0 z-10`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setSidebarOpen(true)}
                variant="ghost"
                size="icon"
                className={`lg:hidden ${colors.textSecondary}`}
              >
                <Menu className="w-6 h-6" />
              </Button>
              <div>
                <h1 className={`text-xl font-bold ${colors.text}`}>⚠️ Logs d'Erreurs</h1>
                <p className={`${colors.textSecondary} text-sm mt-1`}>
                  Surveillance et résolution des erreurs système
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Filter */}
              <select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                className={`px-3 py-1.5 rounded-lg text-sm ${colors.bgCard} ${colors.border} ${colors.text} border`}
              >
                <option value="all">Toutes</option>
                <option value="unresolved">Non résolues</option>
                <option value="resolved">Résolues</option>
              </select>
              <Button 
                onClick={loadErrors} 
                variant="outline" 
                size="sm"
                className={colors.btnOutline}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4">
          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-red-500">
                  {errors.filter(e => !e.resolved).length}
                </div>
                <div className={`text-xs ${colors.textMuted}`}>Non résolues</div>
              </CardContent>
            </Card>
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-green-500">
                  {errors.filter(e => e.resolved).length}
                </div>
                <div className={`text-xs ${colors.textMuted}`}>Résolues</div>
              </CardContent>
            </Card>
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardContent className="p-3 text-center">
                <div className={`text-2xl font-bold ${colors.text}`}>
                  {errors.length}
                </div>
                <div className={`text-xs ${colors.textMuted}`}>Total</div>
              </CardContent>
            </Card>
          </div>

          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${colors.border}`}>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium text-sm`}>Type</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium text-sm`}>Message</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium text-sm`}>Sévérité</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium text-sm`}>Date</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium text-sm`}>Statut</th>
                      <th className={`text-right p-4 ${colors.textSecondary} font-medium text-sm`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className={`p-8 text-center ${colors.textMuted}`}>
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-[#5BA946] border-t-transparent rounded-full animate-spin"></div>
                            Chargement...
                          </div>
                        </td>
                      </tr>
                    ) : errors.length === 0 ? (
                      <tr>
                        <td colSpan={6} className={`p-8 text-center ${colors.textMuted}`}>
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          Aucune erreur trouvée
                        </td>
                      </tr>
                    ) : errors.map((error) => (
                      <tr 
                        key={error.id} 
                        className={`border-b ${colors.border} ${colors.bgHover} transition-colors ${
                          !error.resolved ? (isDark ? 'bg-red-500/5' : 'bg-red-50') : ''
                        }`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {getErrorTypeIcon(error.error_type)}
                            <span className={`${colors.text} text-sm`}>
                              {error.error_type || 'unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className={`${colors.text} text-sm truncate max-w-xs`} title={error.message}>
                            {error.message || '-'}
                          </p>
                        </td>
                        <td className="p-4">
                          {getSeverityBadge(error.severity)}
                        </td>
                        <td className="p-4">
                          <div className={`flex items-center gap-2 ${colors.textSecondary} text-sm`}>
                            <Clock className="w-4 h-4" />
                            {error.timestamp 
                              ? new Date(error.timestamp).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : '-'}
                          </div>
                        </td>
                        <td className="p-4">
                          {error.resolved ? (
                            <Badge className="bg-green-500/20 text-green-500">Résolu</Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-500">Non résolu</Badge>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedError(error)}
                              className={`${colors.textSecondary} hover:text-blue-500`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResolve(error.id, !error.resolved)}
                              className={error.resolved 
                                ? 'text-orange-400 hover:text-orange-300' 
                                : 'text-green-400 hover:text-green-300'
                              }
                            >
                              {error.resolved ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className={`flex items-center justify-between p-4 border-t ${colors.border}`}>
                <p className={`text-sm ${colors.textSecondary}`}>
                  Page {page + 1} • {errors.length} résultats
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={errors.length < limit}
                    onClick={() => setPage(p => p + 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error Detail Modal */}
        {selectedError && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`${colors.bgCard} ${colors.border} max-w-2xl w-full max-h-[80vh] overflow-hidden`}>
              <div className={`p-4 border-b ${colors.border} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  {getErrorTypeIcon(selectedError.error_type)}
                  <div>
                    <h3 className={`${colors.text} font-semibold`}>Détails de l'erreur</h3>
                    <p className={`${colors.textMuted} text-xs`}>ID: {selectedError.id}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedError(null)}>
                  ✕
                </Button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-3">
                <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                  <p className={`text-xs ${colors.textMuted} mb-1`}>Type</p>
                  <p className={colors.text}>{selectedError.error_type || 'unknown'}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                  <p className={`text-xs ${colors.textMuted} mb-1`}>Message</p>
                  <p className={`${colors.text} break-words`}>{selectedError.message || '-'}</p>
                </div>
                <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                  <p className={`text-xs ${colors.textMuted} mb-1`}>Sévérité</p>
                  {getSeverityBadge(selectedError.severity)}
                </div>
                {selectedError.stack_trace && (
                  <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                    <p className={`text-xs ${colors.textMuted} mb-1`}>Stack Trace</p>
                    <pre className={`${colors.text} text-xs overflow-x-auto whitespace-pre-wrap`}>
                      {selectedError.stack_trace}
                    </pre>
                  </div>
                )}
                <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                  <p className={`text-xs ${colors.textMuted} mb-1`}>Date</p>
                  <p className={colors.text}>
                    {selectedError.timestamp 
                      ? new Date(selectedError.timestamp).toLocaleString('fr-FR')
                      : '-'}
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => {
                      handleResolve(selectedError.id, !selectedError.resolved);
                      setSelectedError(null);
                    }}
                    className={selectedError.resolved 
                      ? 'bg-orange-500 hover:bg-orange-600' 
                      : 'bg-green-500 hover:bg-green-600'
                    }
                  >
                    {selectedError.resolved ? 'Marquer non résolu' : 'Marquer résolu'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
